import { FC, useRef, useEffect, useState } from 'react';
import { 
    Dialog, 
    DialogSurface, 
    DialogBody, 
    DialogContent,
    DialogActions, 
    Button, 
    makeStyles, 
    shorthands,
    tokens
} from '@fluentui/react-components';
import SurveyMiniForm, { SurveyFormData } from './SurveyMiniForm';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { Survey, SurveyHeader, SurveyStatus } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import SyButton from '../Common/SyButton';

export interface SurveyMiniFormDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit?: (data: SurveyFormData) => void;
    mode?: 'create' | 'edit';
    surveyId?: string;
}

const useStyles = makeStyles({
    dialogSurface: {
        maxWidth: '600px',
        ...shorthands.borderRadius('16px'),
        ...shorthands.overflow('hidden'),
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.14), 0 0 2px rgba(0, 0, 0, 0.12)',
        ...shorthands.padding(0),
    },
    dialogBody: {
        ...shorthands.padding(0),
        display: 'flex',
        flexDirection: 'column',
    },
    dialogContent: {
        ...shorthands.padding(0),
        flex: '1',
    },
    dialogActions: {
        backgroundColor: tokens.colorNeutralBackground3,
        ...shorthands.padding('16px', '24px'),
        ...shorthands.gap('12px'),
        ...shorthands.margin(0),
        width: '100%',
        justifyContent: 'flex-end',
        display: 'flex',
        flexDirection: 'row',
    },
    cancelButton: {
        backgroundColor: '#ffffff !important',
        '&:hover': {
            backgroundColor: '#f9fafb',
        },
        '&:active': {
            backgroundColor: '#f3f4f6',
        },
    },
});

const SurveyMiniFormDialog: FC<SurveyMiniFormDialogProps> = ({ open, onClose, onSubmit, mode = 'create', surveyId }) => {
    const styles = useStyles();
    // Ref to store form data
    const formDataRef = useRef<SurveyFormData | null>(null);
    const [initialData, setInitialData] = useState<SurveyFormData | undefined>(undefined);
    const [existingSurveyHeader, setExistingSurveyHeader] = useState<SurveyHeader | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [nameError, setNameError] = useState<string | null>(null);

    // Fetch survey header when surveyId changes and mode is edit
    useEffect(() => {
        if (mode === 'edit' && surveyId && open) {
            setIsLoading(true);
            const abortController = new AbortController();
            
            performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    const surveyHeader = await client.getSurveyHeader(surveyId, signal);
                    // Store the full survey header to preserve all fields
                    setExistingSurveyHeader(surveyHeader);
                    setInitialData({
                        surveyName: surveyHeader.title || '',
                        surveySubtitle: surveyHeader.subtitle || '',
                        surveyDescription: surveyHeader.description || '',
                    });
                } catch (error) {
                    console.error('Failed to fetch survey header:', error);
                    setExistingSurveyHeader(null);
                    setInitialData(undefined);
                } finally {
                    setIsLoading(false);
                }
            }, { abortController });

            return () => {
                abortController.abort();
            };
        } else if (mode === 'create') {
            setExistingSurveyHeader(null);
            setInitialData(undefined);
        }
    }, [mode, surveyId, open]);

    // Clear name error when the dialog opens or when initial data changes
    useEffect(() => {
        if (open) {
            setNameError(null);
        }
    }, [open, initialData]);

    // Handler for form data changes
    const handleFormDataChange = (formData: SurveyFormData) => {
        formDataRef.current = formData;
    };

    // Handler for saving survey (create or update)
    const handleSaveSurvey = async () => {
        if (!formDataRef.current) return;

        setIsSaving(true);
        const abortController = new AbortController();
        let validationFailed = false;

        try {
            await performEnterpriseGPTApiCall(async (client, signal) => {
                // Validate survey name uniqueness before saving
                try {
                    const ids = await client.getSurveyIdsByName(formDataRef.current!.surveyName, signal);
                    // For create mode, there must be 0 matches
                    if (mode === 'create') {
                        if (ids && ids.length > 0) {
                            setNameError('This survey name already exists.');
                            validationFailed = true;
                            // prevent save
                            return;
                        }
                    } else {
                        // edit mode: allowed if 0 matches or 1 match with same id
                        if (ids && ids.length > 0) {
                            if (!(ids.length === 1 && surveyId && ids[0] === surveyId)) {
                                setNameError('This survey name already exists.');
                                validationFailed = true;
                                return;
                            }
                        }
                    }
                } catch (err) {
                    // If validation fails unexpectedly, log but allow save to proceed (or alternatively block).
                    console.error('Failed to validate survey name uniqueness:', err);
                }

                if (mode === 'edit' && surveyId && existingSurveyHeader) {
                    // Edit mode: Update survey header while preserving existing fields
                    const updatedSurveyHeader = new SurveyHeader({
                        ...existingSurveyHeader,
                        title: formDataRef.current!.surveyName,
                        subtitle: formDataRef.current!.surveySubtitle,
                        description: formDataRef.current!.surveyDescription,
                    });
                    await client.updateSurveyHeader(updatedSurveyHeader, signal);
                    console.log('Survey header updated successfully');
                } else {
                    // Create mode: Upsert survey
                    const survey = new Survey({
                        title: formDataRef.current!.surveyName,
                        subtitle: formDataRef.current!.surveySubtitle,
                        description: formDataRef.current!.surveyDescription,
                        status: SurveyStatus.Draft,
                    });
                    await client.upsertSurvey(survey, signal);
                    console.log('Survey created successfully');
                }
            }, { abortController });

            // If validation failed earlier, do not proceed to close or call onSubmit
            if (validationFailed) {
                return;
            }

            // Call the optional onSubmit callback if provided
            if (onSubmit) {
                onSubmit(formDataRef.current);
            }

            // Close the dialog on success
            handleClose();
        } catch (error) {
            console.error('Failed to save survey:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsSaving(false);
        }
    };

    const handleNameChange = () => {
        // clear server-side name validation error as user changed the name
        if (nameError) setNameError(null);
    };

    // Handler for closing the dialog
    const handleClose = () => {
        formDataRef.current = null;
        setInitialData(undefined);
        setExistingSurveyHeader(null);
        onClose();
    };

    return (
        <Dialog open={open}>
            <DialogSurface className={styles.dialogSurface}>
                <DialogBody className={styles.dialogBody}>
                    <DialogContent className={styles.dialogContent}>
                        {isLoading ? (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                Loading survey data...
                            </div>
                        ) : (
                            <SurveyMiniForm 
                                onClose={handleClose}
                                onFormDataChange={handleFormDataChange}
                                mode={mode}
                                initialData={initialData}
                                errorMessage={nameError}
                                onNameChange={handleNameChange}
                            />
                        )}
                    </DialogContent>
                </DialogBody>
                <DialogActions className={styles.dialogActions}>
                    <SyButton 
                        appearance='tertiary' 
                        onClick={handleClose} 
                        disabled={isSaving}
                        overrideBackColorLight='white'
                        overrideBackColorDark='#18181b'
                    >
                        Cancel
                    </SyButton>
                    <SyButton appearance='primary' onClick={handleSaveSurvey} disabled={isLoading || isSaving}>
                        {mode === 'edit' ? 'Save' : 'Create a new survey'}
                    </SyButton>
                </DialogActions>
            </DialogSurface>
        </Dialog>
    );
};

export default SurveyMiniFormDialog;
